/**
 * The BINARY-STATE verbs — `open` / `close` (D50), `turn on` / `turn off`
 * (D56) — plus `search`, which is the verb a door stands in front of.
 *
 * @remarks
 * Split out of `action-handlers.ts` in D53, when adding the shut-door refusal
 * pushed that file past the 500-line cap. The seam is a real one rather than
 * an arbitrary halving: these are the only handlers that read or write
 * `openedContainerItemIds` / `switchedOnItemIds` / `searchedContainerItemIds`.
 *
 * D56 is why the header no longer says "container". A door and a switch are one
 * idea pointed two ways — a named binary state, a membership set, and prose
 * that reads differently on each side — so they share {@link spokenTransition}
 * rather than being written twice. A kettle is not a container; what it has in
 * common with a locker is the SHAPE of its state, and that is what this file is
 * about. The filename lags the contents and is worth changing the next time
 * something here moves.
 *
 * `action-handlers.ts` re-exports all three handlers, so no caller changed.
 *
 * @packageDocumentation
 */

import type { ItemId, NarrativeLine, RuntimeState } from '@orpheus/content-schema';
import type { EntityRef } from '@orpheus/parser-engine';

import type { RuntimeAction } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { applicableDoorRefusal } from '../actions/narrow-parsed-command.js';
import { requireItem } from '../bundle/bundle-lookups.js';
import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { nameInSentence, speakSlot } from '../defaults/default-responses.js';
import { isActorPresent, isActorSearchable } from '../entities/actor-presence.js';
import { contentsInSightOf, isAuthoredPlacementPresent } from '../entities/derive-entity-catalog.js';
import { entityRefForItem } from '../entities/entity-ref.js';
import { isContainerOpen, isShutSeeThrough, isSwitchedOn, withMembership, withWorld } from '../entities/item-state.js';
import { type ActionHandlerResult, completed, completedSpeaking, rejected, type SpeakableLine } from './action-handler-result.js';
import { listInSentence } from './dropped-here.js';
import { isItemUnreachable } from './reachability.js';
import { stampNarrativeLines, type TranscriptCursor } from './transcript-cursor.js';

/**
 * `search` with no target, or aimed at the room (D62) — casing where you
 * stand.
 *
 * @remarks
 * A SURVEY, ruled deliberately against the sweep: it names what would reward
 * going through properly and reveals nothing itself, so the per-thing
 * discoveries and authored emptinesses (D50) stay the player's to earn one
 * act at a time. "Worth going through" means a thing that visibly contains
 * something, has its own `searchNarratives`, or has a door — the three
 * declarations that make `search <thing>` a different sentence from
 * `inspect <thing>`.
 *
 * It consumes a turn, exactly as searching a thing does: casing a room is an
 * act, and D62 exists because treating it as a synonym for looking made the
 * verb feel decorative — the player got the description they had already
 * read.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before
 * @param cursor - The turn's transcript cursor
 * @returns The completed step
 */
export const handleSearchRoom = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	// D230 (Calder's POC2): the survey reads a placement through the SAME
	// test the catalog does — taken, removed, or hidden by its
	// `roomVisibilityCondition` is not here — so it can never offer a thing
	// the player cannot then name. Before this, the lit substation's survey
	// listed the seam the breakers had not yet revealed, and `x seam`
	// answered "You see no seam here."
	const searchworthyItems = (bundle.indexes.initialItemIdsByRoomId.get(state.progression.activeRoomId) ?? [])
		.filter((itemId) => isAuthoredPlacementPresent(bundle, state, itemId))
		.map((itemId) => requireItem(bundle, itemId))
		.filter(
			(item) =>
				item.searchNarratives !== undefined ||
				item.openable !== undefined ||
				(bundle.indexes.containedItemIdsByContainerId.get(item.itemId)?.length ?? 0) > 0,
		);
	/*
	 * R30 (Calder's barred-search table; D107): three axes, eight arms, one
	 * law — every world-state gets a true sentence, and the empty line still
	 * cannot lie. The survey's own verb is an OFFER ("worth going through
	 * properly"), so a barred thing never joins its list: what is true of it
	 * is different — seen, and not available — and it gets its own clause
	 * with its own verb. Ordering is his: survey, then barred (both about the
	 * room), then dropped (about YOU — authored as a closing beat). The R25
	 * principle holds for every axis: the sets participate in CHOOSING the
	 * slots, never decorate the one already chosen.
	 */
	const reachableNames = [
		...searchworthyItems.filter((item) => !isItemUnreachable(bundle, state, item.itemId)).map((item) => item.name),
		// D240: a body within reach that carries something, or answers a search in its own words, is worth going through too.
		...bundle.contentPack.actors.filter((actor) => isActorPresent(bundle, state, actor.actorId) && isActorSearchable(bundle, actor)).map((actor) => actor.name),
	];
	const barredNames = searchworthyItems
		.filter((item) => isItemUnreachable(bundle, state, item.itemId))
		.map((item) => item.name);
	const droppedNames = Object.entries(state.world.droppedItemLocations)
		.filter(([, droppedRoomId]) => droppedRoomId === state.progression.activeRoomId)
		.map(([droppedItemId]) => requireItem(bundle, droppedItemId as Parameters<typeof requireItem>[1]).name);
	const spokenLines = [
		...(reachableNames.length === 0
			? []
			: [speakSlot(bundle, state, 'searchRoomSurvey', { itemNames: reachableNames.join(', ') })]),
		...(barredNames.length === 0
			? []
			: [
				speakSlot(
					bundle,
					state,
					reachableNames.length === 0 ? 'searchRoomBarredOnly' : 'searchRoomBarredAlso',
					{ itemNames: barredNames.join(', ') },
				),
			]),
		...(droppedNames.length === 0
			? []
			: [
				speakSlot(
					bundle,
					state,
					reachableNames.length === 0 && barredNames.length === 0 ? 'searchRoomDroppedOnly' : 'searchRoomDroppedAlso',
					{ itemNames: droppedNames.join(', ') },
				),
			]),
	];
	const spokenOrEmpty = spokenLines.length === 0 ? [speakSlot(bundle, state, 'searchRoomEmpty')] : spokenLines;

	return completedSpeaking(bundle, state, cursor, spokenOrEmpty, `search_room:${state.progression.activeRoomId}`);
};

/**
 * `search` (D50) — going THROUGH a thing, which is not the same act as looking
 * at it.
 *
 * @remarks
 * Two outcomes, and the second is the one that decides whether the verb is
 * worth typing anywhere. Finding something reveals it (the concealed contents
 * enter the catalog from here on, because the search is recorded in state),
 * each find in its own `foundNarratives` when it has them (D239), the
 * engine's one sentence otherwise. Only the searched thing's OWN concealed
 * contents are found: a compartment inside a compartment waits for its own
 * search, once its own door is open (D239's "each level by its own act").
 * Finding NOTHING says so ({@link nothingFoundLines}) — in the thing's own
 * words, or in the engine's, which name what it still holds in plain sight.
 * A flat refusal there teaches the player the verb is decorative, which was
 * Calder's first ask and the reason this is not simply an `inspect` alias.
 *
 * The search is recorded either way: a thing you have been through is a thing
 * you have been through, and re-searching should not keep re-revealing.
 */
export const handleSearch = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'search' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const item = requireItem(bundle, action.itemId);

	// D53: a door the CATALOG respects and the handler did not. Derivation
	// already hides a shut container's contents, so `take the keycard` before
	// opening the locker correctly finds no keycard — but `search` reached
	// straight past the same lid and revealed the concealed contents anyway.
	// Two readings of one door is one too many. Found by Calder stress-testing
	// the authored path (D50 content pass §2.2).
	//
	// D253: a door of mesh or glass is shut all the same, and the search still
	// stops at it — but what is plainly behind it is named, not denied.
	if (item.openable !== undefined && !isContainerOpen(item, state)) {
		const seenThrough = isShutSeeThrough(item, state) ? namesInSightOf(bundle, state, entityRefForItem(action.itemId)) : [];

		return rejected(bundle, state,
			{ itemId: action.itemId, type: 'search_while_shut' },
			[
				seenThrough.length === 0
					? speakSlot(bundle, state, 'searchWhileShut', { itemName: item.name })
					: speakSlot(bundle, state, 'searchSeenThrough', { itemName: item.name, itemNames: listInSentence(seenThrough) }),
			],
		);
	}

	const wasAlreadySearched = state.world.searchedContainerItemIds.includes(action.itemId);
	const searchedState: RuntimeState = {
		...state,
		world: {
			...state.world,
			searchedContainerItemIds: wasAlreadySearched
				? state.world.searchedContainerItemIds
				: [...state.world.searchedContainerItemIds, action.itemId],
		},
	};
	// Concealed contents are the reveal; anything already visible was not
	// hidden, so finding it again is not a discovery — and neither is going
	// back through a thing you have already been through. Without the
	// already-searched guard a second `search` announces the same find
	// forever, and a container with ANY concealed contents could never reach
	// its own `searchNarratives`.
	const revealed = wasAlreadySearched
		? []
		: (bundle.indexes.containedItemIdsByContainerId.get(action.itemId) ?? []).filter(
			(containedItemId) => requireItem(bundle, containedItemId).containedIn?.concealed === true,
		);

	if (revealed.length === 0) {
		return completedSpeaking(
			bundle,
			searchedState,
			cursor,
			nothingFoundLines(bundle, state, entityRefForItem(action.itemId), item.name, item.searchNarratives),
			`search:${action.itemId}`,
		);
	}

	// Read after the search is recorded, so a reading keyed on the find is true.
	return completed(searchedState, stampNarrativeLines(bundle, searchedState, cursor, revealLinesOf(bundle, item.name, revealed), `search:${action.itemId}`));
};

/**
 * What a thing or body still holds in plain sight, each named as a sentence names it.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param holderRef - The thing or body
 * @returns The names, in containment order
 */
const namesInSightOf = (bundle: EngineGameBundle, state: RuntimeState, holderRef: EntityRef): readonly string[] =>
	contentsInSightOf(bundle, state, holderRef).map((itemId) => nameInSentence(bundle, requireItem(bundle, itemId).name));

/**
 * What a search that turned up nothing hidden says — of a thing or a body,
 * one builder for both, as {@link revealLinesOf} is for a find.
 *
 * @remarks
 * The searched thing's own `searchNarratives` first (D50). Otherwise the
 * engine's line, and which one is a question of what is still there to see.
 * D247 (Mike, playing the Meridian): the trauma kit, open, the hex key and
 * the sample tube still in it, answered "turn up nothing you did not already
 * have" — measured against the hands, and false of a kit with a key in it.
 * A thing that still holds things in plain sight names them
 * (`searchFoundOnlyInSight`): nothing was hidden, and here is what is not.
 * Only a thing holding nothing the player can see measures the search
 * against the hands — `searchFoundNothing`, or its `…EmptyHanded` twin when
 * they carry nothing (D229, E18: with nothing in the hands the line above
 * read as an inventory line, because it was one).
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the search
 * @param holderRef - The thing or body searched
 * @param holderName - Its display name (the article is the name grammar's, D182)
 * @param searchNarratives - Its own words for a search that finds nothing, if it has them
 * @returns The lines to speak
 */
export const nothingFoundLines = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	holderRef: EntityRef,
	holderName: string,
	searchNarratives: undefined | readonly NarrativeLine[],
): readonly SpeakableLine[] => {
	if (searchNarratives !== undefined) {
		return searchNarratives;
	}

	const inSight = namesInSightOf(bundle, state, holderRef);

	if (inSight.length > 0) {
		return [speakSlot(bundle, state, 'searchFoundOnlyInSight', { itemName: holderName, itemNames: listInSentence(inSight) })];
	}

	return [speakSlot(bundle, state, state.world.inventoryItemIds.length === 0 ? 'searchFoundNothingEmptyHanded' : 'searchFoundNothing', { itemName: holderName })];
};

/**
 * What a search that found things says (D239; D240 for a body's pockets):
 * each find in its own words when the found thing has them, in containment
 * order, the engine's one sentence otherwise.
 *
 * @remarks
 * "Turned over, its back is not its back" is a different sentence from the
 * hidden tab under a pod's skirt, and the engine's one sentence fits
 * neither — so the found thing's `foundNarratives` speak in its place. One
 * builder for a thing searched and a body searched, so the two finds cannot
 * drift into different grammar.
 *
 * @param bundle - The loaded bundle
 * @param searchedName - The searched thing's or body's display name (the article is the name grammar's, D226)
 * @param revealedItemIds - What the search found, in declaration order
 * @returns The lines, one run per find
 */
export const revealLinesOf = (bundle: EngineGameBundle, searchedName: string, revealedItemIds: readonly ItemId[]): readonly NarrativeLine[] =>
	revealedItemIds.flatMap((revealedItemId) => {
		const foundItem = requireItem(bundle, revealedItemId);

		return (
			foundItem.foundNarratives ?? [
				{
					priority: 'standard',
					source: 'environment',
					text: `You go through ${nameInSentence(bundle, searchedName)}, and find ${nameInSentence(bundle, foundItem.name)}.`,
				},
			]
		);
	});

/**
 * `open` / `close` on a thing with a door (D50, re-voiced D135).
 *
 * @remarks
 * The first no-instrument `open` the engine has had — the audit's §1 closes
 * here as a by-product of containment rather than as its own feature.
 *
 * D135 changed what the verbs SAY, twice. (1) A door already in the position
 * asked for did not move, and D120's rule — an action that changes nothing
 * must say so, and must not re-run the narrative of the change — reaches the
 * door verbs, where D120 recorded it as still open. Before, `open` on an open
 * locker re-read its open description as if something had happened. (2) The
 * transition speaks an EVENT — `openedNarratives` / `closedNarratives`, or
 * the flat built-in pair — and never the state description. The old rule
 * ("a door's movement IS its new appearance") had the door verbs read the
 * shut reading aloud, which reads as explaining to the player
 * something they just did (Calder's locker-cluster §3); a sentence that is
 * true of both the movement and the state afterward is a description, and
 * `look` is where descriptions are spoken. The describer still reads the
 * committed state, so opening a thing and then examining it cannot disagree.
 */
export const handleDoor = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'close_container' | 'open_container' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const item = requireItem(bundle, action.itemId);
	const isOpening = action.type === 'open_container';

	if (isContainerOpen(item, state) === isOpening) {
		return rejected(bundle, state,
			{ itemId: item.itemId, type: 'door_unmoved' },
			(isOpening ? item.openable?.alreadyOpenNarratives : item.openable?.alreadyClosedNarratives) ?? [
				speakSlot(bundle, state, isOpening ? 'doorAlreadyOpen' : 'doorAlreadyClosed', { itemName: item.name }),
			],
		);
	}

	const stateAfter = withWorld(state, {
		openedContainerItemIds: withMembership(state.world.openedContainerItemIds, action.itemId, isOpening),
	});

	return spokenTransition(
		bundle,
		stateAfter,
		action,
		cursor,
		(isOpening ? item.openable?.openedNarratives : item.openable?.closedNarratives) ?? [
			speakSlot(bundle, stateAfter, isOpening ? 'doorOpened' : 'doorClosed', { itemName: item.name }),
		],
	);
};

/**
 * The two reasons a switch request never reaches the transition (D65, D120).
 *
 * @remarks
 * Extracted from {@link handleSwitch} when D120's arm pushed it past the
 * complexity cap — and the seam is a real one rather than an arbitrary
 * halving: these are the questions asked BEFORE anything moves, and the
 * handler below is what happens when something does. The power gate stays
 * with the transition on purpose, because a dead device's switch still moves.
 *
 * Order matters. `latched` is about the way BACK and only ever refuses a
 * REVERSAL; `unmoved` is about a request for the position the switch is
 * already in, which is not a return and must never draw the latch's line.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before
 * @param item - The switchable item
 * @param isSwitchingOn - Which way the player asked
 * @returns The refusal, or `undefined` when the switch may move
 */
const refuseUnmovableSwitch = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	item: ReturnType<typeof requireItem>,
	isSwitchingOn: boolean,
): ActionHandlerResult | undefined => {
	const { switchable } = item;
	const isOn = isSwitchedOn(item, state);

	// D65: a LATCHED switch thrown away from its authored start does not come
	// back — refused by construction, the prose only flavoring a refusal the
	// schema already guarantees. Forty dust-welded handles.
	if (switchable?.latching === true && isOn !== (switchable.state === 'on') && isSwitchingOn === (switchable.state === 'on')) {
		return rejected(bundle, state,
			{ itemId: item.itemId, type: 'switch_latched' },
			switchable.latchedNarratives ?? [speakSlot(bundle, state, 'switchIsLatched', { itemName: item.name })],
		);
	}

	/*
	 * D120: a switch already in the requested position DID NOT MOVE, and an
	 * action that changes nothing must not borrow the prose of the change.
	 * Found by Mike, who asked a roaring kettle to come on and was told the
	 * switch went over harder than it should — twice.
	 *
	 * Deliberately before the power gate: whether the device answers is a
	 * question about a switch that moved, and this one did not. Also before the
	 * transition commit, so a no-op cannot re-arm a countdown that a repeatable
	 * trigger (D119) hangs off the real movement.
	 */
	if (isOn === isSwitchingOn) {
		return rejected(bundle, state,
			{ itemId: item.itemId, type: 'switch_unmoved' },
			(isSwitchingOn ? switchable?.alreadyOnNarratives : switchable?.alreadyOffNarratives) ?? [
				speakSlot(bundle, state, isSwitchingOn ? 'switchAlreadyOn' : 'switchAlreadyOff', { itemName: item.name }),
			],
		);
	}

	// D260: a switch held where it is — the lockout tag on the breaker — refused in its own words, before the power gate.
	const heldBy = applicableDoorRefusal(isSwitchingOn ? switchable?.turnOnRefusal : switchable?.turnOffRefusal, bundle, state);

	if (heldBy !== undefined) {
		return rejected(bundle, state, { itemId: item.itemId, type: 'switch_refused' }, heldBy.narratives);
	}

	return undefined;
};

/**
 * `turn X on` / `turn X off` (D56) — the switch verbs.
 *
 * @remarks
 * The door handler with one word changed, which is the whole claim of D56's
 * modeling: a door and a switch are one idea — a named binary state, a
 * membership set, and prose that reads differently on each side — so they
 * share {@link spokenTransition} rather than being written twice.
 *
 * What a thing LOOKS like and what happens WHEN it moves are different
 * questions (see `switchableSchema`). This handler speaks the second — the
 * authored `turnOnNarratives` / `turnOffNarratives`, or the slot when the pack
 * wrote none. The first is a `switched_on` variant on the item's description,
 * and `inspect` asks for it separately.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before
 * @param action - The narrowed switch action
 * @param cursor - The turn's transcript cursor
 * @returns The completed step
 */
export const handleSwitch = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'switch_off' | 'switch_on' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const item = requireItem(bundle, action.itemId);
	const isSwitchingOn = action.type === 'switch_on';
	// Not `?.` by preference: narrowing already proved this item declares a
	// switch (only `narrowSwitchCommand` mints these actions, and it checks).
	const { switchable } = item;

	const refusal = refuseUnmovableSwitch(bundle, state, item, isSwitchingOn);

	if (refusal !== undefined) {
		return refusal;
	}

	// D65: the power gate. The switch MOVES either way — the fiction lives in
	// the gap between the switch and the power — but a dead device does not
	// answer, so the transition narrative yields to the dead beat.
	const isPowered = switchable?.poweredWhen === undefined || evaluateCondition(switchable.poweredWhen, bundle, state);
	const authoredTransitionLines = isSwitchingOn ? switchable?.turnOnNarratives : switchable?.turnOffNarratives;
	const spokenLines: readonly SpeakableLine[] = isPowered
		? (authoredTransitionLines ?? [speakSlot(bundle, state, isSwitchingOn ? 'switchedOn' : 'switchedOff', { itemName: item.name })])
		: (switchable.deadNarratives ?? [speakSlot(bundle, state, 'deviceIsDead', { itemName: item.name })]);

	// A switch's movement is an EVENT, not an appearance: "starts to tick, then
	// to roar" is what happened, and what the kettle looks like while running is
	// a conditional variant on its description. Separating them is the whole
	// reason `switchable` ships without per-state prose.
	return spokenTransition(
		bundle,
		withWorld(state, { switchedOnItemIds: withMembership(state.world.switchedOnItemIds, action.itemId, isSwitchingOn) }),
		action,
		cursor,
		spokenLines,
	);
};

/**
 * A binary state moved: commit it and speak, stamped with this verb's trigger
 * context.
 *
 * @remarks
 * The shared shape behind all four binary-state verbs. What each pair SAYS is
 * the caller's, and deliberately so — a door's movement is an appearance and a
 * switch's is an event — but committing the state and stamping the entry is one
 * idea, written once.
 *
 * @param bundle - The loaded bundle (the stamp resolves conditional text)
 * @param stateAfter - The state the transition produced
 * @param action - The action, for its trigger context
 * @param cursor - The turn's transcript cursor
 * @param speakable - What this verb says about what just happened — authored lines or slot responses
 * @returns The completed step
 */
const spokenTransition = (
	bundle: EngineGameBundle,
	stateAfter: RuntimeState,
	action: Extract<RuntimeAction, { type: 'close_container' | 'open_container' | 'switch_off' | 'switch_on' }>,
	cursor: TranscriptCursor,
	speakable: readonly SpeakableLine[],
): ActionHandlerResult => completedSpeaking(bundle, stateAfter, cursor, speakable, `${action.type}:${action.itemId}`);

